import React, { useEffect, useState } from "react";
import {
  Box, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Text, HStack, Badge, useColorModeValue, Heading,
} from "@chakra-ui/react";
import axios from "axios";

interface QuestionSetAuditEntry {
  timestamp: string;
  user: string | null;
  action: string;
  sequence_number: number;
  old_question_id: number;
  new_question_id: number;
  reason: string;
}

interface QuestionSetAuditLogProps {
  questionSetId: number;
}

const QuestionSetAuditLog: React.FC<QuestionSetAuditLogProps> = ({ questionSetId }) => {
  const [entries, setEntries] = useState<QuestionSetAuditEntry[]>([]);

  const panelBg = useColorModeValue("gray.50", "gray.800");
  const mutedText = useColorModeValue("gray.500", "gray.400");

  useEffect(() => {
    const fetchAuditLog = async () => {
      try {
        const apiurl = process.env.REACT_APP_API_URL || "https://default-url.com";
        // Assumes the existing GET question-set-details endpoint now returns
        // `audit_log` per the schemas.py addition above. Adjust the URL below
        // if audit_log is exposed via a different endpoint in your codebase.
        const response = await axios.get(`${apiurl}/qset/${questionSetId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        setEntries(response.data.audit_log ?? []);
      } catch (error) {
        console.error("Error fetching question set audit log:", error);
      }
    };
    fetchAuditLog();
  }, [questionSetId]);

  if (entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => b.timestamp.localeCompare(a.timestamp));

  return (
    <Box bg={panelBg} borderRadius="lg" p={3} mb={4}>
      <Heading size="sm" mb={2} px={2}>Audit Log ({sorted.length})</Heading>
      <Accordion allowMultiple>
        {sorted.map((entry, idx) => (
          <AccordionItem key={idx} border="none">
            <AccordionButton borderRadius="md" px={2} py={2}>
              <HStack flex="1" spacing={3} textAlign="left">
                <Badge colorScheme="orange" fontSize="10px">OVERRIDE</Badge>
                <Text fontSize="xs" color={mutedText}>
                  {new Date(entry.timestamp).toLocaleString()}
                </Text>
                <Text fontSize="xs">
                  Seq #{entry.sequence_number}: Q{entry.old_question_id} → Q{entry.new_question_id}
                </Text>
                {entry.user && (
                  <Text fontSize="xs" color={mutedText}>by {entry.user}</Text>
                )}
              </HStack>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={3} px={4}>
              <Text fontSize="sm">{entry.reason}</Text>
            </AccordionPanel>
          </AccordionItem>
        ))}
      </Accordion>
    </Box>
  );
};

export default QuestionSetAuditLog;